import { type ReactNode, useCallback, useMemo } from 'react';

import { useHoveredOfferActionsContext } from '@/app/shared/context/HoveredOfferContext';
import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { listingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout';
import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';
import { memoWithDisplayName } from '@/packages/utils';

import { CardLayout } from '../../components/CardLayout';
import { useCardOfferContext } from '../../context/selectors';
import { useCardClickAction } from '../../hooks/card/useCardClickAction';
import { type TInitCardApiContext } from '../../types/TCardApiContext';
import { type TCardContextData } from '../../types/TCardContextData';
import { CardContext } from '../CardContext';

import { ContentContainer } from './containers/ContentContainer';
import { GalleryContainer } from './containers/GalleryContainer';
import { PublisherContainer } from './containers/PublisherContainer';

type TResidentialCardContainerProps = TCardContextData & {
  initCardApi: TInitCardApiContext;
};

export function CardContainer(): ReactNode {
  const {
    data: {
      extendedProperties: { offerServices },
      geo: { geoHash },
    },
    id: offerId,
  } = useCardOfferContext();

  const { onOfferHover, onOfferBlur } = useHoveredOfferActionsContext();

  const activePinId = useActivePinIdSelector();
  const reduxState = useReduxState();

  const handleCardClick = useCardClickAction();

  const handleCardMouseEnter = useCallback(() => {
    const isNarrow = listingLayoutIsNarrowSelector(reduxState.getState());

    if (!isNarrow || Boolean(activePinId)) {
      return;
    }

    onOfferHover(geoHash);
  }, [activePinId, geoHash, reduxState, onOfferHover]);

  return (
    <CardLayout
      content={<ContentContainer />}
      data-testid={`card.${offerId}`}
      gallery={<GalleryContainer />}
      isTop={offerServices?.isTop3}
      publisher={<PublisherContainer />}
      onClick={handleCardClick}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={onOfferBlur}
    />
  );
}

export const ResidentialCardContainer = memoWithDisplayName<TResidentialCardContainerProps>(
  ({ dealType, index, offer, initCardApi }): ReactNode => {
    const cardContextData = useMemo(() => ({ dealType, index, offer }), [dealType, index, offer]);

    return (
      <CardContext cardContextData={cardContextData} initCardApi={initCardApi}>
        <CardContainer />
      </CardContext>
    );
  },
  'ResidentialCardContainer',
);
